import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    StateBreadcrumbs,
    StateCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as StateActions from "../../../actions/states";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./StatePage.scss";

interface StatePageProps {
    form: Dtos.State & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    state: Dtos.State;
    lookups: Dtos.Lookup[];
    loadingState: boolean;
    loadStateFailure: boolean;
    loadStateErrorCode: string;
    savingState: boolean;
    saveStateSuccess: boolean;
    saveStateFailure: boolean;
    creatingState: boolean;
    createStateSuccess: boolean;
    validatingState: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearState: StateActions.IStateClearActionCreator;
    loadState: StateActions.IStateLoadByIdActionCreator;
    saveState: StateActions.IStateSaveActionCreator;
    createState: StateActions.IStateCreateActionCreator;
    validateState: StateActions.IStateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.state";

const StatePage: FC<StatePageProps> = ({
    form,
    formState,
    formProperties,
    state,
    lookups,
    loadingState,
    loadStateFailure,
    loadStateErrorCode,
    savingState,
    saveStateSuccess,
    saveStateFailure,
    creatingState,
    createStateSuccess,
    validatingState,
    validationFailures,
    permissions,
    clearState,
    loadState,
    saveState,
    createState,
    validateState,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { stateId } = useParams<{ stateId: string }>();
    const stateIdNumber = parseInt(stateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.State = {} as Dtos.State;

        loadForm(reduxFormName, Object.assign({ ...state }, extraData));
    };


    // Load country
    useEffect(() => {
        if (stateIdNumber && !state && !loadingState) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(StateBreadcrumbs(stateIdNumber));
            loadState(stateIdNumber);
        } else if (!stateIdNumber && !creatingState && !createStateSuccess) {
            createState();
            setBreadcrumbs(StateCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (state && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        stateIdNumber,
        state,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        StateBreadcrumbs,
        loadState,
        createState,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingState,
        creatingState,
        createStateSuccess,
        StateCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadStateFailure) {
            navigate(`/error/${loadStateErrorCode}`);
        }
    }, [loadStateFailure, loadStateErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (stateIdNumber && state?.value) {
            setBreadcrumbs(StateBreadcrumbs(stateIdNumber, state?.value));
        }
    }, [stateIdNumber, state, setBreadcrumbs, StateBreadcrumbs]);

    // Reload state after saving
    useEffect(() => {
        if (!savingState && saveStateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>State saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/states";
                navigate(url);
            } else if (!stateIdNumber) {
                let url: string = `/settings/general/states/${state?.id}`;
                navigate(url);
                clearState();
            }
        }
    }, [
        savingState,
        saveStateSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingState && saveStateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingState, saveStateFailure]);

    // Cleanup effect to clear the state on component unmount
    useEffect(() => {
        return () => {
            clearState();
            resetForm(reduxFormName);
        };
    }, [clearState, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/states";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.State) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.State = {} as Dtos.State;

            await saveState(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.State = {} as Dtos.State;

            if (form && hasBeenSubmitted && !validatingState) {
                await validateState(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.StatesUpdate,
                Dtos.Permission.StatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingState ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "state",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "state",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "state",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "state",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingState,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingState}
            permission={Dtos.Permission.StatesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<StatePageProps> => {
    let data: Dtos.State | undefined = !(state.states.formData instanceof Array)
        ? state.states.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        state: data!,
        loadingState:
            state.states.loadState &&
            state.states.loadState.status == RequestState.Pending,
        loadStateFailure:
            state.states.loadState &&
            state.states.loadState.status == RequestState.Failure,
        loadStateErrorCode: state.states.loadState.errorCode,
        savingState:
            state.states.saveState &&
            state.states.saveState.status == RequestState.Pending,
        saveStateSuccess:
            state.states.saveState &&
            state.states.saveState.status == RequestState.Success,
        saveStateFailure:
            state.states.saveState &&
            state.states.saveState.status == RequestState.Failure,
        creatingState:
            state.states.createState &&
            state.states.createState.status == RequestState.Pending,
        createStateSuccess:
            state.states.createState &&
            state.states.createState.status == RequestState.Success,
        validatingState:
            state.states.validateState &&
            state.states.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.state,
        formState: state.states.formState,
        formProperties: state.states.formProperties,
        validationFailures: state ? state.states.validationFailures : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<StatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadState: bindActionCreators(StateActions.LoadStateById, dispatch),
        clearState: bindActionCreators(StateActions.Clear, dispatch),
        saveState: bindActionCreators(StateActions.SaveState, dispatch),
        createState: bindActionCreators(StateActions.CreateState, dispatch),
        validateState: bindActionCreators(StateActions.ValidateState, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(StatePage);
